<script setup lang="ts">
/**
 * Playground v2 — the AIRA column (Figma `Chat` 114:54823 empty, 114:55029 in conversation).
 *
 * As drawn: AIRA answers as plain text next to a 40px avatar, the shopper's line sits in a
 * `rgba(125,109,186,.12)` bubble with a 2px bottom-right corner, and every turn above the
 * last one is dimmed to 60%. The hero (copy + figure + AI button) belongs to an untouched
 * canvas; once a thread exists it gives way to the thread.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AIRA_FULL_RIVE_SRC, AiraRiveFigure } from '@/features/aira-widget'
import airaAvatar from '@/assets/style-space/playground/aira-avatar.png'
import iconAi from '@/ui/icons/main/ai.svg?url'
import iconClose from '@/ui/icons/main/close.svg?url'
// the file's own 20x20 icons: the project's are drawn edge-to-edge and read oversized here
import iconPlus from '@/assets/style-space/playground/icon-plus.svg?url'
import iconSend from '@/assets/style-space/playground/icon-send.svg?url'
import type {
  FairStyleSpaceAiraChipAction,
  FairStyleSpaceAiraMessage,
} from './fair-style-space-aira.data'
import {
  resolveFairStyleSpaceImage,
  type FairStyleSpaceWardrobeItem,
} from './fair-style-space-wardrobe.data'
import {
  FAIR_SS2_PLAYGROUND_HERO,
  FAIR_SS2_PLAYGROUND_LOOK_CHIP_DELAY_MS,
  FAIR_SS2_PLAYGROUND_LOOK_CHIPS,
  type FairStyleSpaceV2PlaygroundState,
} from './fair-style-space-v2-playground.data'

const props = defineProps<{
  state: FairStyleSpaceV2PlaygroundState
  messages: FairStyleSpaceAiraMessage[]
  hasConversation: boolean
  /** The piece the next line is about — the pill over the input (Figma `Chat Input`). Lifted by its × only. */
  selectedItem: FairStyleSpaceWardrobeItem | null
  placeholder?: string
  sending?: boolean
  /** The occasion of the deal — "Three outfits for Work are ready" (Figma `Playground Generating`). */
  occasionLabel?: string
  /** A deal running past 10 s — "Still working on it…" under the title. */
  slow?: boolean
  /** Fewer outfits than asked came back — the title counts them. */
  resultCount?: number
  /** Editing a saved outfit: one AIRA line, no mascot, no quick chips (entry 3). */
  editing?: boolean
}>()

const emit = defineEmits<{
  send: [text: string]
  chip: [action: FairStyleSpaceAiraChipAction]
  retry: [clientMessageId: string]
  surprise: []
  'dress-today': []
  complete: []
  'clear-context': []
  /** The capsule card (`capsule_ready`) → its page. */
  'open-capsule': [capsuleId: number]
  /** Entry 8: the collapsed `propose_outfits` card → the canvas in the choose state. */
  'open-outfits': [messageId: string]
}>()

const { t } = useI18n()

const draft = ref('')
const bodyRef = ref<HTMLElement | null>(null)

/** `multi` ask (Figma "Recommended answer in chat-many"): the checked chips per line, until Done. */
const checked = ref<Record<string, string[]>>({})

/** "3 outfits · Dinner", or just the count when the deal had no occasion. */
function proposalLabel(proposal: NonNullable<FairStyleSpaceAiraMessage['proposal']>): string {
  const count = t('fair_style_space.playground_v2.proposed_outfits_count', { count: proposal.count })
  return proposal.occasionLabel ? `${count} · ${proposal.occasionLabel}` : count
}

function isChecked(messageId: string, chipId: string) {
  return (checked.value[messageId] ?? []).includes(chipId)
}

function toggleChecked(messageId: string, chipId: string) {
  const current = checked.value[messageId] ?? []
  checked.value = {
    ...checked.value,
    [messageId]: current.includes(chipId) ? current.filter((id) => id !== chipId) : [...current, chipId],
  }
}

/** Done: the chosen labels become the shopper's line, the values go into the brief. */
function submitChecked(message: FairStyleSpaceAiraMessage) {
  const ids = checked.value[message.id] ?? []
  const chips = (message.chips ?? []).filter((chip) => ids.includes(chip.id))
  if (!chips.length) return
  emit('chip', {
    kind: 'answer-multi',
    text: chips.map((chip) => chip.label ?? '').join(', '),
    answerKey: message.askAnswerKey ?? null,
    values: chips.map((chip) => chip.value ?? chip.label ?? ''),
  })
}

/** Only the newest line's chips are live — a past question has been answered. */
function chipsLive(index: number) {
  return index === props.messages.length - 1
}

/**
 * The hero holds the column until the conversation starts; the loading frame brings it back
 * (mascot + "Putting your outfits together…"). The choose frame is ONE line with the avatar
 * at the bottom, no mascot (`Choose - Default`).
 */
const showHero = computed(() => !props.hasConversation || props.state === 'generating-loading')

const showThread = computed(() => props.hasConversation && props.state !== 'generating-loading')

const showChooseLine = computed(() => props.state === 'generating-results')

const hero = computed(() => FAIR_SS2_PLAYGROUND_HERO[props.state])

/** "Three outfits for Work are ready" / "Three outfits are ready" / "Two outfits for Work are ready". */
const heroTitle = computed(() => {
  if (props.editing && (props.state === 'look-complete' || props.state === 'chat-replace-flow')) {
    return t('fair_style_space.playground_v2.hero_title_editing')
  }
  if (props.state !== 'generating-results') {
    return t(`fair_style_space.playground_v2.${hero.value.titleKey}`)
  }
  const count = props.resultCount ?? 3
  const key = props.occasionLabel ? 'hero_title_results' : 'hero_title_results_none'
  return t(`fair_style_space.playground_v2.${key}`, {
    occasion: props.occasionLabel ?? '',
    count: t(`fair_style_space.playground_v2.count_${Math.min(Math.max(count, 1), 3)}`),
  })
})

/** One chip (`What's missing?`) after a pause on a look in the making — AIRA does not nag. */
const chipsReady = ref(false)
let chipTimer: ReturnType<typeof setTimeout> | null = null
watch(
  () => props.state,
  (state) => {
    if (chipTimer) clearTimeout(chipTimer)
    chipsReady.value = false
    if (state !== 'look-complete') return
    chipTimer = setTimeout(() => {
      chipsReady.value = true
    }, FAIR_SS2_PLAYGROUND_LOOK_CHIP_DELAY_MS)
  },
  { immediate: true },
)
onBeforeUnmount(() => {
  if (chipTimer) clearTimeout(chipTimer)
})

const lookChips = computed(() =>
  props.state === 'look-complete' && chipsReady.value && !props.editing
    ? FAIR_SS2_PLAYGROUND_LOOK_CHIPS.map((chip) => ({
        id: chip.id,
        label: t(`fair_style_space.playground_v2.${chip.labelKey}`),
      }))
    : [],
)

function textOf(message: FairStyleSpaceAiraMessage): string {
  if (message.rawText) return message.rawText
  if (!message.textKey) return ''
  return t(`fair_style_space.playground.${message.textKey}`, message.textParams ?? {})
}

/** The thread opens on the newest line, the way a conversation is read. */
watch(
  () => [props.messages.length, props.state],
  () => {
    void nextTick(() => {
      const body = bodyRef.value
      if (body) body.scrollTop = body.scrollHeight
    })
  },
  { immediate: true },
)

function submit() {
  const text = draft.value.trim()
  if (!text || props.sending) return
  draft.value = ''
  emit('send', text)
}
</script>

<template>
  <section
    class="fair-ss2-pg-chat"
    :class="{ 'fair-ss2-pg-chat--thread': showThread }"
    :aria-label="t('fair_style_space.playground_v2.chat_label')"
  >
    <div
      ref="bodyRef"
      class="fair-ss2-pg-chat__body"
    >
      <template v-if="showHero">
        <div class="fair-ss2-pg-chat__hero">
          <p class="fair-ss2-pg-chat__hero-title">
            {{ heroTitle }}
          </p>
          <p
            v-if="slow && state === 'generating-loading'"
            class="fair-ss2-pg-chat__hero-subtitle"
          >
            {{ t('fair_style_space.playground_v2.hero_still_working') }}
          </p>
          <p
            v-if="hero.subtitleKey"
            class="fair-ss2-pg-chat__hero-subtitle"
          >
            {{ t(`fair_style_space.playground_v2.${hero.subtitleKey}`) }}
          </p>
        </div>

        <AiraRiveFigure
          v-if="!editing"
          class="fair-ss2-pg-chat__aira"
          :src="AIRA_FULL_RIVE_SRC"
          :width="109"
          :height="299"
          lazy
        />

        <button
          v-if="hero.actions.includes('surprise')"
          type="button"
          class="fair-ss2-pg-chat__ai-btn"
          :disabled="sending"
          @click="emit('surprise')"
        >
          <img
            class="fair-ss2-pg__icon"
            :src="iconAi"
            alt=""
          >
          {{ t('fair_style_space.playground_v2.surprise_me') }}
        </button>

        <button
          v-if="hero.actions.includes('complete')"
          type="button"
          class="fair-ss2-pg-chat__ai-btn"
          :disabled="sending"
          @click="emit('complete')"
        >
          <img
            class="fair-ss2-pg__icon"
            :src="iconAi"
            alt=""
          >
          {{ t('fair_style_space.playground_v2.complete_look') }}
        </button>

        <button
          v-if="hero.actions.includes('dress-today')"
          type="button"
          class="fair-ss2-pg-chat__ai-btn"
          :disabled="sending"
          @click="emit('dress-today')"
        >
          <img
            class="fair-ss2-pg__icon"
            :src="iconAi"
            alt=""
          >
          {{ t('fair_style_space.playground_v2.dress_me_today') }}
        </button>

        <div
          v-if="lookChips.length"
          class="fair-ss2-pg-chat__chips"
        >
          <button
            v-for="chip in lookChips"
            :key="chip.id"
            type="button"
            class="fair-ss2-pg-chat__chip"
            @click="emit('chip', { kind: 'say', text: chip.label })"
          >
            <img
              class="fair-ss2-pg__icon"
              :src="iconAi"
              alt=""
            >
            {{ chip.label }}
          </button>
        </div>
      </template>

      <div
        v-if="showThread"
        class="fair-ss2-pg-chat__thread-list"
      />
      <div
        v-for="(message, index) in messages"
        :key="message.id"
        class="fair-ss2-pg-chat__line"
        :class="{
          'fair-ss2-pg-chat__line--user': message.role === 'user',
          'fair-ss2-pg-chat__line--past': index < messages.length - 1,
          'fair-ss2-pg-chat__line--failed': message.status === 'failed',
        }"
      >
        <!-- AIRA answers next to her avatar, with no bubble around the words -->
        <template v-if="message.role === 'aira'">
          <span class="fair-ss2-pg-chat__avatar">
            <img
              class="fair-ss2-pg-chat__avatar-img"
              :src="airaAvatar"
              alt=""
            >
          </span>

          <div class="fair-ss2-pg-chat__line-body">
            <p class="fair-ss2-pg-chat__text">
              {{ textOf(message) }}
            </p>

            <div
              v-if="message.chips?.length"
              class="fair-ss2-pg-chat__chips"
            >
              <button
                v-for="chip in message.chips"
                :key="chip.id"
                type="button"
                class="fair-ss2-pg-chat__chip"
                :class="{
                  'fair-ss2-pg-chat__chip--checked':
                    message.askMode === 'multi' && isChecked(message.id, chip.id),
                }"
                :disabled="!chipsLive(index)"
                :aria-pressed="message.askMode === 'multi' ? isChecked(message.id, chip.id) : undefined"
                @click="
                  message.askMode === 'multi'
                    ? toggleChecked(message.id, chip.id)
                    : emit('chip', chip.action)
                "
              >
                <img
                  v-if="chip.icon === 'ai' && message.askMode !== 'multi'"
                  class="fair-ss2-pg__icon"
                  :src="iconAi"
                  alt=""
                >
                <span
                  v-if="message.askMode === 'multi' && isChecked(message.id, chip.id)"
                  class="fair-ss2-pg-chat__chip-check"
                  aria-hidden="true"
                >✓</span>
                {{ chip.label ?? t(`fair_style_space.playground.${chip.labelKey}`) }}
              </button>
            </div>

            <div
              v-if="message.askMode === 'multi' && chipsLive(index)"
              class="fair-ss2-pg-chat__done-row"
            >
              <button
                type="button"
                class="fair-ss2-pg-chat__done"
                :disabled="!(checked[message.id]?.length)"
                @click="submitChecked(message)"
              >
                {{ t('fair_style_space.playground_v2.chat_done') }}
              </button>
            </div>

            <!-- Figma entry 8: three outfits as ONE card; the canvas does the choosing -->
            <button
              v-if="message.proposal"
              type="button"
              class="fair-ss2-pg-chat__proposal"
              :aria-label="t('fair_style_space.playground_v2.open_proposed_outfits', { count: message.proposal.count })"
              @click="emit('open-outfits', message.id)"
            >
              <span class="fair-ss2-pg-chat__proposal-stage">
                <span
                  v-for="preview in message.proposal.previews"
                  :key="preview.id"
                  class="fair-ss2-pg-chat__proposal-preview"
                >
                  <img
                    v-for="thumb in preview.thumbs"
                    :key="thumb.id"
                    class="fair-ss2-pg-chat__proposal-piece"
                    :src="resolveFairStyleSpaceImage(thumb.imageSrc)"
                    alt=""
                    loading="lazy"
                  >
                </span>
              </span>
              <span class="fair-ss2-pg-chat__capsule-row">
                <span class="fair-ss2-pg-chat__capsule-name">
                  {{ proposalLabel(message.proposal) }}
                </span>
                <span
                  class="fair-ss2-pg-chat__capsule-arrow"
                  aria-hidden="true"
                >→</span>
              </span>
            </button>

            <!-- Figma "Capsule preview in chat": the built capsule, → its page -->
            <button
              v-if="message.capsule"
              type="button"
              class="fair-ss2-pg-chat__capsule"
              :aria-label="t('fair_style_space.playground_v2.open_capsule', { name: message.capsule.name })"
              @click="emit('open-capsule', message.capsule.capsuleId)"
            >
              <span class="fair-ss2-pg-chat__capsule-stage">
                <img
                  v-for="thumb in message.capsule.thumbs"
                  :key="thumb.id"
                  class="fair-ss2-pg-chat__capsule-piece"
                  :src="resolveFairStyleSpaceImage(thumb.imageSrc)"
                  alt=""
                  loading="lazy"
                >
              </span>
              <span class="fair-ss2-pg-chat__capsule-row">
                <span class="fair-ss2-pg-chat__capsule-name">{{ message.capsule.name }}</span>
                <span
                  class="fair-ss2-pg-chat__capsule-arrow"
                  aria-hidden="true"
                >→</span>
              </span>
            </button>
          </div>
        </template>

        <div
          v-else
          class="fair-ss2-pg-chat__bubble"
        >
          <p class="fair-ss2-pg-chat__text">
            {{ textOf(message) }}
          </p>
          <button
            v-if="message.retryClientMessageId"
            type="button"
            class="fair-ss2-pg-chat__retry"
            @click="emit('retry', message.retryClientMessageId)"
          >
            {{ t('fair_style_space.playground.chat_try_again') }}
          </button>
        </div>
      </div>
      <!-- the choose state: "Three outfits for Work are ready. Pick the one you like" -->
      <div
        v-if="showChooseLine"
        class="fair-ss2-pg-chat__line fair-ss2-pg-chat__line--choose"
      >
        <span class="fair-ss2-pg-chat__avatar">
          <img
            class="fair-ss2-pg-chat__avatar-img"
            :src="airaAvatar"
            alt=""
          >
        </span>
        <div class="fair-ss2-pg-chat__line-body">
          <p class="fair-ss2-pg-chat__text">
            {{ heroTitle }}. {{ t('fair_style_space.playground_v2.hero_subtitle_results') }}
          </p>
        </div>
      </div>
    </div>

    <div class="fair-ss2-pg-chat__composer">
      <div
        v-if="selectedItem"
        class="fair-ss2-pg-chat__context"
      >
        <img
          class="fair-ss2-pg-chat__context-img"
          :src="resolveFairStyleSpaceImage(selectedItem.imageSrc)"
          :alt="selectedItem.name"
        >
        {{ selectedItem.name }}
        <button
          type="button"
          class="fair-ss2-pg-chat__context-clear"
          :aria-label="t('fair_style_space.playground_v2.context_clear')"
          @click="emit('clear-context')"
        >
          <img
            class="fair-ss2-pg__icon"
            :src="iconClose"
            alt=""
          >
        </button>
      </div>

      <div class="fair-ss2-pg-chat__input-row">
        <button
          type="button"
          class="fair-ss2-pg-chat__icon-btn"
          :aria-label="t('fair_style_space.playground_v2.attach')"
        >
          <img
            :src="iconPlus"
            alt=""
            width="20"
            height="20"
          >
        </button>

        <input
          v-model="draft"
          class="fair-ss2-pg-chat__input"
          type="text"
          :placeholder="placeholder ?? t('fair_style_space.playground_v2.ask_aira')"
          data-testid="ss2-playground-chat-input"
          @keydown.enter.prevent="submit"
        >

        <button
          type="button"
          class="fair-ss2-pg-chat__icon-btn"
          :disabled="!draft.trim() || sending"
          :aria-label="t('fair_style_space.playground_v2.send')"
          @click="submit"
        >
          <img
            :src="iconSend"
            alt=""
            width="20"
            height="20"
          >
        </button>
      </div>
    </div>
  </section>
</template>
